<!--
  CollectorShopPage — /collector/shops/:supplierId/ — «المحل», one shop of the
  collector's day (collector-app phase 1, handoff spec Section 2.2; mockup
  Shop.dc.html).

  Reads the shop out of collectorDayStore.day (the backend's document). The
  header says where the shop sits on the route and how many lines are still
  unmarked; the amber strip lists returns to hand back (read-only — the shop's
  answer is the leader's to record); then one row per line.

  THE LINES (phase 1 prompt 4): the shop's lines fold by `groupKey`
  (collector/lineView.js `foldLines`) into CollectorLineCard — a grouped card
  with one row per member, a single-line card, or a dashed card for a line the
  leader released or moved. The card only reports what was tapped; every tap
  goes through the action layer (collector/actions.js) from here. Buttons show
  only when the backend says `canMark`. A line parked on a member question has
  no mark buttons at all: once she answers, «اشتريته» / «مش لقيته» are REPORTS
  (`bought` / `not_found`, collector-app P3-C) the leader's close acts on. A
  day not started or closed is said once, above the lines.

  THE MONEY (collector-clarity P3-A): the sticky header carries the shop's
  paid / needed / still-to-buy strip (CollectorShopMoney), recalculated from
  the rebased day on every mark — nothing to wire.

  THREE SHEETS, ONE AT A TIME (memory add-to-box-ux-audit: no sheet over a
  sheet): the line detail (photos, both bars, the tries), «بلّغ من الرف», and
  «خلصت المحل». They are page-level singletons; moving from one to another
  closes the first and opens the second when it has closed. The sheets read the
  line and the shop LIVE from the day, so a mark shows at once.

  LEAVING: F7 removes the day page when this one is pushed, and after a reload
  router.back() would leave the app (fabricated history) — so back() only when
  this page arrived by an in-app push (`page.from === 'next'`), otherwise
  navigate to the day (memory f7-pushed-page-and-reload-back; TadbeesaPage).

  PHASE 2 (collector-app P2-B): every tap here is written to the phone before
  it shows (collector/actions.js); while the login is out the card «افتح يومك
  من تيليجرام» sits under the header (L-D); inside the Telegram Mini App the
  page is ONE screen, «افتح يومك في كروم», with no sheet, footer, storage or
  request (L-A).

  ROUTE INVARIANTS (playbook Section 5): EAGER `component:` (row 16),
  requireCollector with App.vue's EAGER_COLLECTOR_PREFIX for the cold guard
  skip (row 22), `meta.hideTabbar: true`, slashless normalizer after (row 20).
-->
<template>
  <f7-page
    name="collector-shop"
    class="collector-surface collector-shop"
    @page:beforein="onPageBeforeIn"
  >
    <collector-tma-screen v-if="inTma"></collector-tma-screen>
    <template v-else>
      <header class="c-shop-head">
        <div class="c-shop-head__row">
          <button
            type="button"
            class="c-reset c-shop-head__back"
            :aria-label="t('collector.shop.back')"
            @click="leave"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              class="c-flip-rtl"
            ><path d="M9 6l6 6-6 6" /></svg>
          </button>
          <div class="c-shop-head__text">
            <h1 class="c-shop-head__title">
              {{ shop ? shopName : t('collector.day.title') }}
            </h1>
            <div v-if="shop" class="c-shop-head__sub">
              {{ headerSubline }}
            </div>
            <!-- The shop's own supplier app, read-only, on his phone (supplier-
                 preview P2-A): only for a known shop — the lines with no shop
                 carry supplierId null. Never in the Mini App (the whole page is
                 the one Telegram screen there). -->
            <button
              v-if="canPreview"
              type="button"
              class="c-reset c-shop-head__preview"
              :disabled="preview.busy.value"
              @click="confirmPreview"
            >
              {{ t('collector.preview.showButton') }}
            </button>
          </div>
          <collector-sync-pill compact></collector-sync-pill>
        </div>
        <p v-if="shop?.paidByWallet" class="c-shop-wallet-note">{{ t('collector.shopRow.walletShop') }}</p>
        <collector-shop-money
          v-if="shop && !shop.paidByWallet"
          :shop="shop"
          :cash-with-me-cents="adviceCashCents"
        ></collector-shop-money>
      </header>

      <collector-auth-card></collector-auth-card>

      <template v-if="shop">
        <!-- A day not started (or closed) locks every line for the same reason:
           said once here, not under each row. -->
        <div v-if="lockedText" class="c-day-locked">
          {{ lockedText }}
        </div>

        <section v-if="returns.length" class="c-returns">
          <div class="c-returns__title">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            ><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
            <span>{{ t(`collector.count.returnsTitle.${countBucket(returns.length)}`, { n: returns.length }) }}</span>
          </div>
          <div v-for="(ret, index) in returns" :key="index" class="c-returns__line">
            {{ returnLine(ret) }}
          </div>
          <div class="c-returns__footer">
            {{ t('collector.shop.returnsFooter') }}
          </div>
        </section>

        <div class="c-lines">
          <collector-line-card
            v-for="card in cards"
            :key="card.key"
            :card="card"
            @mark="mark"
            @mark-rest="markRest"
            @reopen="(line) => mark(line, 'reopen')"
            @report="(line) => goSheet('report', line.id)"
            @report-answer="reportAnswer"
            @open="(line) => goSheet('detail', line.id)"
          ></collector-line-card>
        </div>
      </template>

      <div v-else class="c-shop-state">
        {{ dayStore.status === 'ready' ? t('collector.shop.notFound') : t('collector.day.loading') }}
      </div>
    </template>

    <template #fixed>
      <div v-if="shop && !inTma" class="c-shop-foot">
        <button type="button" class="c-reset c-shop-foot__done" @click="goSheet('done')">
          <span>{{ t('collector.shop.done') }}</span>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        </button>
        <div v-if="pendingCount > 0" class="c-shop-foot__helper">
          {{ doneHelper }}
        </div>
      </div>
    </template>

    <collector-line-detail-sheet
      v-if="!inTma"
      :opened="sheet === 'detail'"
      :line="sheetLine"
      @update:opened="(open) => { if (!open) closeSheet(); }"
      @mark="mark"
      @reopen="(line) => mark(line, 'reopen')"
      @report="(line) => goSheet('report', line.id)"
      @report-answer="reportAnswer"
    ></collector-line-detail-sheet>

    <collector-report-sheet
      v-if="!inTma"
      :opened="sheet === 'report'"
      :line="sheetLine"
      @update:opened="(open) => { if (!open) closeSheet(); }"
      @send="sendReport"
    ></collector-report-sheet>

    <collector-done-sheet
      v-if="!inTma"
      :opened="sheet === 'done'"
      :shop="shop"
      :plan-id="dayStore.activePlanId"
      :cash-with-me-cents="cashWithMeCents"
      @update:opened="(open) => { if (!open) closeSheet(); }"
      @mark-oos="markOutOfStock"
      @leave-shop="leaveShop"
      @filed="onFiled"
    ></collector-done-sheet>
  </f7-page>
</template>

<script setup>
import '@/collector/collector.css';
import { useDocumentVisibility } from '@vueuse/core';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCollectorActionsStore } from '@/collector/actions.js';
import { countBucket, countPhrase, phraseText } from '@/collector/dayView.js';
import { foldLines } from '@/collector/lineView.js';
import { cashKnown } from '@/collector/moneyView.js';
import CollectorAuthCard from '@/components/collector/CollectorAuthCard.vue';
import CollectorDoneSheet from '@/components/collector/CollectorDoneSheet.vue';
import CollectorLineCard from '@/components/collector/CollectorLineCard.vue';
import CollectorLineDetailSheet from '@/components/collector/CollectorLineDetailSheet.vue';
import CollectorReportSheet from '@/components/collector/CollectorReportSheet.vue';
import CollectorShopMoney from '@/components/collector/CollectorShopMoney.vue';
import CollectorSyncPill from '@/components/collector/CollectorSyncPill.vue';
import CollectorTmaScreen from '@/components/collector/CollectorTmaScreen.vue';
import { confirmAndEnter } from '@/composables/previewConfirm.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { useSupplierPreview } from '@/composables/useSupplierPreview.js';
import { isTma } from '@/platform/index.js';
import { useCollectorDayStore } from '@/stores/collectorDayStore.js';

const props = defineProps({
  f7route: {
    type: Object,
    default: null,
  },
  f7router: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const { formatAmount } = useCurrency();
const dayStore = useCollectorDayStore();
const actions = useCollectorActionsStore();
// Read once: isTma() re-checks several signals on every call (L-A screen).
const inTma = isTma();

const pounds = (cents) => formatAmount(cents, { trimZeros: true });

const shop = computed(() => dayStore.findShop(props.f7route?.params?.supplierId));
const shopName = computed(() => shop.value?.name ?? t('collector.shops.noShop'));

// ---- «اعرض عليه التطبيق» — the shop's supplier app, read-only (supplier-preview P2-A)
const preview = useSupplierPreview();
const canPreview = computed(() => !inTma && shop.value?.supplierId != null);

function confirmPreview() {
  confirmAndEnter(preview.enter, { id: shop.value?.supplierId, name: shopName.value });
}
const lines = computed(() => shop.value?.lines ?? []);
const cards = computed(() => foldLines(lines.value));
const returns = computed(() => shop.value?.returnsToHandBack ?? []);
const pendingCount = computed(() => Number(shop.value?.counters?.pending) || 0);
const planStatus = computed(() => dayStore.day?.plan?.status ?? null);

/** `cash.withMeCents` right now — «الكاش معاك بعد المحل» in the done sheet reads it. */
const cashWithMeCents = computed(() => Number(dayStore.day?.cash?.withMeCents) || 0);
/**
 * The cash the money strip's advisory line compares with — null when the leader
 * recorded no allocation (the phone then does not know what he holds, and the
 * strip says nothing about it).
 */
const adviceCashCents = computed(() => (cashKnown(dayStore.day) ? cashWithMeCents.value : null));

/** «المحل 4 من 13 · باقي صنفين» — one short line, so the money strip below it fits (spec Section 6.5). */
const headerSubline = computed(() => {
  const current = shop.value;
  if (!current) return '';
  const { position } = current;
  const total = dayStore.shops.length;
  if (pendingCount.value > 0) {
    return t('collector.shop.sublineLeft', {
      position,
      total,
      items: phraseText(countPhrase('items', pendingCount.value), t, pounds),
    });
  }
  return t('collector.shop.sublineAll', {
    position,
    total,
    items: phraseText(countPhrase('items', current.counters?.total ?? 0), t, pounds),
  });
});

const doneHelper = computed(() =>
  phraseText({ key: 'collector.shop.doneHelper', nested: countPhrase('items', pendingCount.value) }, t, pounds),
);

/** «فستان سواريه · كود 1156 · قطعة · استبدال» */
function returnLine(ret) {
  return t('collector.shop.returnLine', {
    product: ret?.product ?? '—',
    code: ret?.memberCode ?? '—',
    qty: phraseText(countPhrase('pieces', ret?.qty ?? 1), t, pounds),
    kind: t(`collector.shop.returnKind.${ret?.kind === 'exchange' ? 'exchange' : 'refund'}`),
  });
}

/** One strip for the whole shop when the plan is not running (marks refused). */
const lockedText = computed(() => {
  if (planStatus.value === 'confirmed') return t('collector.line.planNotStarted');
  if (planStatus.value === 'completed') return t('collector.line.planClosed');
  return null;
});

// ---- Taps ---------------------------------------------------------------------
//
// Every tap goes through the action layer: it shows at once (the day re-renders
// with it applied) and is sent as a batch; no confirm — «ارجّع» is the undo.

function mark(line, type) {
  actions.enqueue({ type, planItemId: line.id });
  // A mark made from the detail sheet ends that visit: back to the list.
  if (sheet.value === 'detail' && type !== 'reopen') closeSheet();
}

/**
 * «اشتريته» / «مش لقيته» on her answer (L-F): a REPORT to the leader — `bought`
 * or `not_found` — that never marks the line; his close at the station applies
 * her option. One tap, no confirm; like a mark made from the detail sheet, it
 * ends that visit.
 */
function reportAnswer(line, type) {
  actions.enqueue({ type, planItemId: line.id });
  if (sheet.value === 'detail') closeSheet();
}

/** «اتجاب الباقي كله»: one `collected` per row still pending — a mark marks ONE row. */
function markRest(rows) {
  for (const line of rows) actions.enqueue({ type: 'collected', planItemId: line.id });
}

/** «ابعت للّيدر»: ONE `facts` tap; the line stays unmarked. */
function sendReport(line, payload) {
  actions.enqueue({ type: 'facts', planItemId: line.id, payload });
  closeSheet();
}

/** The done sheet's «علّمهم مش موجود»: one `out_of_stock` per pending line. */
function markOutOfStock(rows) {
  for (const line of rows) actions.enqueue({ type: 'out_of_stock', planItemId: line.id });
}

/** «المحل مقفول — سيبهم لليدر»: ONE `shop_left` (any line of mine there names the shop). */
function leaveShop(current) {
  const anchor = (current?.lines ?? []).find((line) => !line.movedAway) ?? current?.lines?.[0];
  if (!anchor || current?.supplierId == null) return;
  actions.enqueue({
    type: 'shop_left',
    planItemId: anchor.id,
    payload: { supplierId: Number(current.supplierId), reason: 'closed' },
  });
  closeSheet();
}

/**
 * The invoice is on the phone (collector/invoiceQueue.js sends it): the sheet
 * closes. The re-read is harmless with no signal; the shop's state arrives from
 * the API once the filing is answered — the queue asks for that read itself.
 */
function onFiled() {
  closeSheet();
  dayStore.refresh();
}

// ---- Sheets: one at a time ----------------------------------------------------

/** 'none' | 'detail' | 'report' | 'done' */
const sheet = ref('none');
const sheetLineId = ref(null);
let queued = null;

/** The line a sheet shows, LIVE from the day (null if it left the day). */
const sheetLine = computed(() => lines.value.find((line) => String(line.id) === String(sheetLineId.value)) ?? null);

/**
 * Open a sheet. When another one is open it closes first and the new one opens
 * once the close has landed (`@sheet:closed` → onSheetClosed) — never two at
 * once.
 */
function goSheet(name, lineId = null) {
  if (sheet.value !== 'none' && sheet.value !== name) {
    queued = { name, lineId };
    sheet.value = 'none';
    return;
  }
  if (lineId != null) sheetLineId.value = lineId;
  sheet.value = name;
}

function closeSheet() {
  queued = null;
  sheet.value = 'none';
}

/** Each sheet's `opened` goes false on close; the queued one (if any) opens now. */
watch(sheet, async (value) => {
  if (value !== 'none' || !queued) return;
  const next = queued;
  queued = null;
  await nextTick();
  // One tick after the close: F7 has started the closing animation; opening the
  // next sheet now cross-fades them instead of stacking.
  setTimeout(() => goSheet(next.name, next.lineId), 250);
});

// ---- Arriving -----------------------------------------------------------------

onMounted(() => {
  if (inTma) return;
  actions.start();
  const wantedPlan = props.f7route?.query?.plan;
  if (wantedPlan && String(wantedPlan) !== dayStore.activePlanId) {
    dayStore.selectPlan(wantedPlan);
  } else if (!dayStore.day) {
    dayStore.load();
  } else {
    dayStore.refresh();
  }
});

const visibility = useDocumentVisibility();
watch(visibility, (state) => {
  if (state === 'visible' && !inTma) dayStore.refresh();
});

function onPageBeforeIn(page) {
  pushedInApp = page?.from === 'next';
}

// ---- Leaving ------------------------------------------------------------------
//
// router.back() is only safe after an in-app push: on a reload F7's
// browserHistory boot fabricates ['/', '<url>'] and back() calls the browser's
// History.back(), which leaves the app. `from: 'next'` marks a forward push.
let pushedInApp = false;

function leave() {
  const router = props.f7router;
  const previous = router?.history?.[router.history.length - 2];
  if (pushedInApp && previous?.split('?')[0] === '/collector/') router.back();
  else router?.navigate('/collector/', { clearPreviousHistory: true });
}
</script>

<style scoped>
.c-shop-head {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: calc(var(--f7-safe-area-top, 0px) + 18px) 16px 16px;
  background: var(--c-indigo);
}

.c-shop-head__row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.c-shop-head__back {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--c-radius-chip);
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.c-shop-head__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-grow: 1;
  min-width: 0;
}

.c-shop-head__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  /* the header is sticky: every pixel of it is taken from the lines */
  line-height: 1.3;
  color: #ffffff;
}

.c-shop-head__sub {
  font-size: 13px;
  color: var(--c-head-sub);
}

/* A ghost pill under the subline: the sticky header keeps its one row. */
.c-shop-head__preview {
  align-self: flex-start;
  margin-top: 6px;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
}

.c-shop-head__preview:disabled {
  opacity: 0.6;
}

.c-day-locked {
  margin: 12px 16px 0;
  padding: 10px 14px;
  border-radius: var(--c-radius-button);
  background: var(--c-lilac);
  color: var(--c-indigo);
  font-size: 13px;
  font-weight: 700;
}

.c-returns {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 16px 0;
  padding: 12px 14px;
  background: var(--c-amber-fill);
  border: 1px solid var(--c-amber-border);
  border-radius: var(--c-radius-button);
}

.c-returns__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--c-amber-text);
}

.c-returns__line {
  font-size: 13px;
  color: var(--c-amber-ink);
}

.c-returns__footer {
  font-size: 12px;
  color: var(--c-amber-text);
}

.c-lines {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 12px 16px 0;
  /* clears the fixed «خلصت المحل» footer */
  padding-bottom: calc(var(--f7-safe-area-bottom, 0px) + 120px);
}

.c-shop-state {
  padding: 32px 16px;
  color: var(--c-muted);
  text-align: center;
}

.c-shop-foot {
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px 16px calc(var(--f7-safe-area-bottom, 0px) + 18px);
  background: linear-gradient(to top, var(--c-ground) 70%, rgba(244, 242, 248, 0));
}

.c-shop-foot__done {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 56px;
  border-radius: var(--c-radius-row);
  background: var(--c-indigo);
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
}

.c-shop-foot__helper {
  font-size: 12px;
  color: var(--c-muted);
  text-align: center;
}

</style>

<!-- non-scoped: the back arrow points the way home, which flips with the
     reading direction on <html> (CLAUDE.md: never :global(ancestor) .child). -->
<style>
[dir='rtl'] .collector-shop .c-flip-rtl {
  transform: scaleX(1);
}

[dir='ltr'] .collector-shop .c-flip-rtl {
  transform: scaleX(-1);
}

.c-shop-wallet-note {
  margin: 6px 0 0;
  color: var(--poolit-warning-600, #b45309);
  font-size: 13px;
  font-weight: 600;
}
</style>
